import { Button } from '@fluentui/react-components'
import { Add16Regular, Delete16Regular, Edit16Regular, WebAsset20Regular } from '@fluentui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ErrorComponent } from '@/ui/ErrorComponent'
import { FileTemplateForm } from '@/ui/Forms/FileTemplateForm'
import { Loading } from '@/ui/Loading'
import { OverlayDrawerComponent } from '@/ui/OverlayDrawerComponent/OverlayDrawerComponent'

type MessagesEntityTypesExistingEntityType = RestSchemas['Messages.EntityTypes.ExistingEntityType']
type MessagesTemplatesFileTemplate = RestSchemas['Messages.Templates.FileTemplate']

export function FileTemplatesList({
  entityType,
  applicationId,
}: {
  entityType: MessagesEntityTypesExistingEntityType
  applicationId: number
}) {
  const queryClient = useQueryClient()
  const [drawerOpen, setDrawerOpen] = useState<'none' | 'addTemplate'>('none')
  const { getTemplatesApiListFileTemplates } = createRestClient()

  const {
    data: fileTemplates,
    isPending,
    isError,
  } = getTemplatesApiListFileTemplates.useQuery({
    query: { applicationId: applicationId, entityTypeId: entityType.id },
  })

  const invalidateTemplates = useCallback(
    () =>
      getTemplatesApiListFileTemplates.invalidateQueries(
        { parameters: { query: { applicationId: applicationId, entityTypeId: entityType.id } } },
        queryClient
      ),
    [entityType.id, getTemplatesApiListFileTemplates, queryClient, applicationId]
  )

  if (isPending) return <Loading />
  if (isError) return <ErrorComponent />

  // if (fileTemplates.length === 0) return <div>(No file templates found)</div>

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center justify-between">
        <h6 className="text-sm">File Templates:</h6>
        <Button
          size="small"
          appearance="primary"
          shape="circular"
          icon={<Add16Regular />}
          onClick={() => setDrawerOpen('addTemplate')}
        />
      </div>
      <ul className="w-full divide-y divide-gray-100 font-light">
        {fileTemplates.length > 0 ? (
          fileTemplates.map((ft) => (
            <ListItem key={ft.id} fileTemplate={ft} entityType={entityType} invalidateTemplates={invalidateTemplates} />
          ))
        ) : (
          <div>(No file templates found)</div>
        )}

        <OverlayDrawerComponent
          open={drawerOpen === 'addTemplate'}
          size="medium"
          setOpen={(open) => setDrawerOpen(open ? 'addTemplate' : 'none')}
          title="Add File Template"
          content={
            <FileTemplateForm
              applicationId={entityType.applicationId}
              selectedEntityType={entityType}
              onSubmit={() => {
                setDrawerOpen('none')
                invalidateTemplates()
              }}
            />
          }
        />
      </ul>
    </div>
  )
}

function ListItem({
  fileTemplate,
  entityType,
  invalidateTemplates,
}: {
  fileTemplate: MessagesTemplatesFileTemplate
  entityType: MessagesEntityTypesExistingEntityType
  invalidateTemplates: () => void
}) {
  const [drawerOpen, setDrawerOpen] = useState<'none' | 'editTemplate' | 'deleteTemplate'>('none')
  return (
    <>
      <li key={fileTemplate.id} className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <WebAsset20Regular />
          {fileTemplate.name}
        </div>
        <div className="flex items-center">
          <Button
            size="small"
            appearance="subtle"
            shape="circular"
            icon={<Edit16Regular />}
            onClick={() => setDrawerOpen('editTemplate')}
          />
          <Button
            size="small"
            appearance="subtle"
            shape="circular"
            icon={<Delete16Regular />}
            onClick={() => setDrawerOpen('deleteTemplate')}
          />
        </div>
      </li>

      <OverlayDrawerComponent
        open={drawerOpen === 'editTemplate'}
        size="medium"
        setOpen={(open) => setDrawerOpen(open ? 'editTemplate' : 'none')}
        title="Edit File Template"
        content={
          <FileTemplateForm
            edit
            applicationId={entityType.applicationId}
            selectedEntityType={entityType}
            selectedTemplate={fileTemplate}
            invalidateTemplates={invalidateTemplates}
            onSubmit={() => {
              setDrawerOpen('none')
              invalidateTemplates()
            }}
            onDelete={() => {
              setDrawerOpen('none')
              // invalidateTemplates()
            }}
          />
        }
      />

      <OverlayDrawerComponent
        open={drawerOpen === 'deleteTemplate'}
        size="medium"
        setOpen={(open) => setDrawerOpen(open ? 'deleteTemplate' : 'none')}
        title="Delete File Template"
        content={
          <FileTemplateForm
            delete
            applicationId={entityType.applicationId}
            selectedEntityType={entityType}
            selectedTemplate={fileTemplate}
            onSubmit={() => {
              setDrawerOpen('none')
              // invalidateTemplates()
            }}
            onDelete={() => {
              setDrawerOpen('none')
              invalidateTemplates()
            }}
          />
        }
      />
    </>
  )
}
